import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import API from "utils/api";
import { manufacturerLogoUrl } from "config/images.js";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import green from "@material-ui/core/colors/green";
import red from "@material-ui/core/colors/red";
import brown from "@material-ui/core/colors/brown";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import styles from "../claimCheckPageStyle.js";
import { fetchCertTypes } from "utils/ugcCertTypes";
import CertString from "components/UltraGreenCert/CertString";

const useStyles = makeStyles(styles);

const TableCellCustom = withStyles((theme) => ({
  head: {
    backgroundColor: "#38c754",
    color: theme.palette.common.white,
  },
  body: {},
}))(TableCell);

const CheckCircleOutlineIconGreen = withStyles({
  root: { color: green[400] },
})((props) => (
  <Tooltip title="Approved by UltraGreen Certification standard!" placement="right-start">
    <CheckCircleOutlineIcon color="default" {...props} />
  </Tooltip>
));

const BlockIconRed = withStyles({
  root: { color: red[400] },
})((props) => (
  <Tooltip title="Prohibited by UltraGreen Certification standard!" placement="right-start">
    <BlockIcon color="default" {...props} />
  </Tooltip>
));

const HelpOutlineIconBrown = withStyles({
  root: { color: brown[200] },
})((props) => (
  <Tooltip title="Custom classification needed!" placement="right-start">
    <HelpOutlineIcon color="default" {...props} />
  </Tooltip>
));

export default function SearchSection({ t, urlquery }) {
  const classes = useStyles();
  const searchRef = useRef(null);

  const [state, setState] = useState({
    pageNum: "0",
    loading: false,
    searchResult: [],
    resultSizeIngSub: 0,
    resultSizeBrands: 0,
    searchText: "",
    resultInfoText: t.searchStarting,
  });
  const [count, setCount] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [stateQuery, setStateQuery] = useState("");
  const [certTypes, setCertTypes] = useState([]);

  useEffect(() => {
    let live = true;
    fetchCertTypes().then((types) => {
      if (live) setCertTypes(types);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // The QR deep link used to be applied inside the .then() of a POST to
    // /ultragreen/hb — a liveness ping whose controller was deleted from
    // cosahh-rest in April 2026. Because an unmapped path on that API
    // authenticates rather than 404s, the call has answered 401 ever since,
    // the .then() never ran, and ?urlquery= silently stopped working. The
    // ping returned a constant string nobody read, so it is removed rather
    // than restored: nothing here should depend on it.
    if (urlquery) {
      setStateQuery(urlquery);
      if (searchRef.current) {
        searchRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  useEffect(() => {
    const query = stateQuery;
    setCount((prev) => prev + 1);
    if (query === "" || query === undefined || query === null || count === 0) {
      setState({
        loading: false,
        searchResult: [],
        resultSizeIngSub: 0,
        resultSizeBrands: 0,
        searchText: "",
        resultInfoText: t.searchStarting,
      });
      return;
    }
    if (query.length < 4 && query.length > 0) {
      setState((prev) => ({
        ...prev,
        resultInfoText: t.searchTooShort,
      }));
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    API.get("search/all/" + stateQuery + "/0/50/cosahh.com")
      .then(function (response) {
        var allRepos = response.data;
        var ingSubList = response.data.resultIngSubs;
        var brandsList = response.data.resultBrands;
        const sizeIngSub = ingSubList.length;
        const sizeBrands = brandsList.length;
        setState({
          loading: false,
          searchResult: allRepos,
          resultSizeIngSub: sizeIngSub,
          resultSizeBrands: sizeBrands,
          searchText: "",
          resultInfoText: sizeIngSub === 0 && sizeBrands === 0 ? t.searchNoResults : "",
        });
      })
      .catch(function () {});
  }, [stateQuery]);

  const handleChangeText = (event) => {
    setState({ ...state, searchText: event.target.value });
  };

  const onKeyUp = (event) => {
    if (event.charCode === 13) {
      startSearching();
    }
  };

  const startSearching = () => {
    const query = state.searchText;
    setState((prev) => ({ ...prev, loading: true }));
    setStateQuery(query);
  };

  const IngredientCard = ({ ingredient }) => (
    <Card style={{ marginBottom: "16px" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <a
            style={{ color: "#38c754", textDecoration: "none" }}
            href={"https://www.skinlyzer.com/ingredient/" + ingredient.authId}
            target="_blank"
            rel="noopener noreferrer"
          >
            {ingredient.inci}
          </a>
        </Typography>
        <Divider style={{ margin: "12px 0" }} />
        <Box mb={1}>
          <Typography variant="body2" color="textSecondary">
            <strong>CAS:</strong> {ingredient.cas}
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography variant="body2" color="textSecondary">
            <strong>EC:</strong> {ingredient.ec}
          </Typography>
        </Box>
        <Box mb={1} display="flex" alignItems="center">
          <Typography variant="body2" color="textSecondary" style={{ marginRight: "8px" }}>
            <strong>Cosmetic Status:</strong>
          </Typography>
          {ingredient.ultraGreenScore === 0 && ingredient.ultraGreenScore !== undefined && <HelpOutlineIconBrown />}
          {ingredient.ultraGreenScore === 1 && <CheckCircleOutlineIconGreen />}
          {ingredient.ultraGreenScore === 2 && <BlockIconRed />}
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color="textSecondary" style={{ marginRight: "8px" }}>
            <strong>Detergent Status:</strong>
          </Typography>
          {ingredient.ultraGreenScoreDetergent === 0 && ingredient.ultraGreenScoreDetergent !== undefined && <HelpOutlineIconBrown />}
          {ingredient.ultraGreenScoreDetergent === 1 && <CheckCircleOutlineIconGreen />}
          {ingredient.ultraGreenScoreDetergent === 2 && <BlockIconRed />}
        </Box>
      </CardContent>
    </Card>
  );

  const BrandCard = ({ brand }) => (
    <Card style={{ marginBottom: "16px" }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <img
            src={manufacturerLogoUrl(brand.logoUrl)}
            width="80"
            alt={brand.name}
            style={{ marginRight: "12px" }}
          />
          <Typography variant="h6">
            <Link
              to={"/brand/" + encodeURIComponent(brand.authId)}
              style={{ color: "#38c754", textDecoration: "none" }}
            >
              {brand.name}
            </Link>
          </Typography>
        </Box>
        <Divider style={{ margin: "12px 0" }} />
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Description:</strong><br />
            [HU] {brand.description}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary">
            <strong>Certification Status:</strong>
          </Typography>
          <Box mt={1}>
            <CertString brand={brand} types={certTypes} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ingsubElements =
    !state.loading && state.searchResult.resultIngSubs
      ? state.searchResult.resultIngSubs.map((n) => (
          <TableRow key={n.id} className={classes.row}>
            <TableCell component="th" scope="row" className={classes.cell}>
              <a
                style={{ color: "#555" }}
                href={"https://www.skinlyzer.com/ingredient/" + n.authId}
                target="_blank"
                rel="noopener noreferrer"
              >
                {n.inci}
              </a>
            </TableCell>
            <TableCell className={classes.cell}>{n.cas}</TableCell>
            <TableCell className={classes.cell}>{n.ec}</TableCell>
            <TableCell className={classes.cellHead}>
              {n.ultraGreenScore === 0 && n.ultraGreenScore !== undefined && <HelpOutlineIconBrown />}
              {n.ultraGreenScore === 1 && <CheckCircleOutlineIconGreen />}
              {n.ultraGreenScore === 2 && <BlockIconRed />}
            </TableCell>
            <TableCell className={classes.cellHead}>
              {n.ultraGreenScoreDetergent === 0 && n.ultraGreenScoreDetergent !== undefined && <HelpOutlineIconBrown />}
              {n.ultraGreenScoreDetergent === 1 && <CheckCircleOutlineIconGreen />}
              {n.ultraGreenScoreDetergent === 2 && <BlockIconRed />}
            </TableCell>
          </TableRow>
        ))
      : null;

  const brandsElements =
    !state.loading && state.searchResult.resultBrands
      ? state.searchResult.resultBrands.map((b) => (
          <TableRow key={b.id} className={classes.row}>
            <TableCell className={classes.cell}>
              <img
                src={manufacturerLogoUrl(b.logoUrl)}
                width="100"
                alt={b.name}
              />
              <Link to={"/brand/" + encodeURIComponent(b.authId)}>
                &nbsp;{b.name}
              </Link>
            </TableCell>
            <TableCell component="th" scope="row" className={classes.cell}>
              [HU] {b.description}
            </TableCell>
            <TableCell className={classes.cellHead}>
              <CertString brand={b} types={certTypes} />
            </TableCell>
          </TableRow>
        ))
      : null;

  return (
    <div className={classes.searchSection} ref={searchRef} id="search">
      <div className={classes.searchHeader}>
        <h2 className={classes.searchTitle}>{t.searchTitle}</h2>
        <p className={classes.searchSubtitle}>{t.searchSubtitle}</p>
      </div>
      <div className={classes.searchResults}>
        <div style={{ paddingBottom: 30, maxWidth: 600, margin: "0 auto" }}>
          <TextField
            id="searchText"
            label={t.searchPlaceholder}
            onChange={handleChangeText}
            value={state.searchText}
            onKeyPress={onKeyUp}
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={startSearching} aria-label="search">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <p className={classes.searchQrNote}>{t.searchQrNote}</p>
        </div>

        {state.resultSizeBrands > 0 && (
          <div>
            <Typography variant="h5" className={classes.subTitle}>
              {t.brandsSectionTitle}
            </Typography>
            {!isMobile && (
              <Table className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCellCustom style={{ width: "30%" }}>Brand Name</TableCellCustom>
                    <TableCellCustom style={{ width: "50%" }}>Description</TableCellCustom>
                    <TableCellCustom className={classes.cellHead}>Certification Status</TableCellCustom>
                  </TableRow>
                </TableHead>
                <TableBody>{brandsElements}</TableBody>
              </Table>
            )}
            {isMobile && state.searchResult.resultBrands && (
              <div>
                {state.searchResult.resultBrands.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} />
                ))}
              </div>
            )}
            <Typography variant="h5" className={classes.subTitle2}></Typography>
          </div>
        )}

        {state.resultSizeIngSub > 0 && (
          <div>
            <Typography variant="h5" className={classes.subTitle}>
              {t.ingredientsSectionTitle}
            </Typography>
            {!isMobile && (
              <Table className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCellCustom style={{ width: "50%" }}>INCI</TableCellCustom>
                    <TableCellCustom>CAS</TableCellCustom>
                    <TableCellCustom>EC</TableCellCustom>
                    <TableCellCustom className={classes.cellHead}>Cosmetic Status</TableCellCustom>
                    <TableCellCustom className={classes.cellHead}>Detergent Status</TableCellCustom>
                  </TableRow>
                </TableHead>
                <TableBody>{ingsubElements}</TableBody>
              </Table>
            )}
            {isMobile && state.searchResult.resultIngSubs && (
              <div>
                {state.searchResult.resultIngSubs.map((ingredient) => (
                  <IngredientCard key={ingredient.id} ingredient={ingredient} />
                ))}
              </div>
            )}
          </div>
        )}

        {(state.resultSizeIngSub > 0 || state.resultSizeBrands > 0) && (
          <div style={{ paddingTop: 20 }}>
            <Typography variant="caption" display="block" gutterBottom>
              {t.searchHint}
            </Typography>
          </div>
        )}

        {state.loading && <LinearProgress />}

        <div style={{ paddingTop: 20, paddingBottom: 30 }}>
          <Typography align="center" variant="body1" gutterBottom>
            {state.resultInfoText}
          </Typography>
        </div>

        <div style={{ paddingBottom: 30 }}>
          <Typography variant="caption" display="block" gutterBottom>
            {t.searchDisclaimer1}
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            {t.searchDisclaimer2}
          </Typography>
        </div>
      </div>
    </div>
  );
}
